/*
  Reviews popup and on-page rating links.

  [data-reviews-open]  Opens the reviews popup (falls back to its href without JavaScript).
  [data-reviews-link]  Store-wide rating link: scrolls to this page's testimonials when it has
                       them, otherwise opens the popup. Its href is the Reviews page.

  The popup content is the torque-reviews-modal section, fetched once through the Section
  Rendering API. Judge.me's loader only scans the page when it first loads, so after inserting
  the widgets we run their data scripts and load the loader again to render them. The popup
  footer always links to the Reviews page in case Judge.me changes how that works.
*/
(() => {
  if (window.TorqueReviewsModal) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const TESTIMONIALS = '#testimonials, #drive-testimonials, [data-testimonials]';
  let dialog;
  let loaded = false;
  let lastTrigger;

  // Judge.me attaches its Write a review form and photo viewer to <body>. A modal <dialog>
  // sits above everything there and makes it inert, so while the popup is open those overlays
  // are moved inside it, and put back when it closes.
  const JDGM_OVERLAY = '.jdgm-review-widget-modal, .jdgm-write-review-modal, [class*="jdgm"][class*="lightbox"], [class*="jdgm"][class*="media-modal"]';
  const adopted = new Set();
  const adoptOverlays = () => {
    if (!dialog || !dialog.open) return;
    document.querySelectorAll(JDGM_OVERLAY).forEach((el) => {
      if (dialog.contains(el) || el.parentElement !== document.body) return;
      adopted.add(el);
      dialog.appendChild(el);
    });
  };
  const overlayObserver = new MutationObserver(adoptOverlays);
  const releaseOverlays = () => {
    overlayObserver.disconnect();
    adopted.forEach((el) => { if (dialog.contains(el)) document.body.appendChild(el); });
    adopted.clear();
  };

  const build = (reviewsUrl) => {
    dialog = document.createElement('dialog');
    dialog.className = 'tq-reviews-modal';
    dialog.setAttribute('aria-labelledby', 'TqReviewsModalTitle');
    dialog.innerHTML =
      '<div class="tq-reviews-modal__frame">' +
        '<div class="tq-reviews-modal__head"><h2 id="TqReviewsModalTitle">Customer reviews</h2>' +
        '<button type="button" class="tq-reviews-modal__close" aria-label="Close reviews">&times;</button></div>' +
        '<div class="tq-reviews-modal__body" tabindex="-1"><p class="tq-reviews-modal__status" role="status">Loading reviews…</p></div>' +
        '<div class="tq-reviews-modal__foot"><a class="rlink" href="' + reviewsUrl + '">Open the reviews page</a></div>' +
      '</div>';
    document.body.appendChild(dialog);
    dialog.querySelector('.tq-reviews-modal__close').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
    // Escape closes an open Judge.me form first, then the popup on the next press.
    dialog.addEventListener('cancel', (event) => {
      const overlay = [...adopted].find((el) => dialog.contains(el) && el.checkVisibility && el.checkVisibility());
      if (!overlay) return;
      event.preventDefault();
      const close = overlay.querySelector('[class*="close"]');
      if (close) close.click();
    });
    dialog.addEventListener('close', () => {
      releaseOverlays();
      document.documentElement.classList.remove('tq-reviews-open');
      if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
    });
    // In-popup jump links scroll the popup, not the page.
    dialog.addEventListener('click', (event) => {
      const jump = event.target.closest('a[href^="#popup-reviews-"]');
      if (!jump) return;
      const target = dialog.querySelector(jump.getAttribute('href'));
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const hydrateJudgeMe = (container) => {
    container.querySelectorAll('script').forEach((old) => {
      const script = document.createElement('script');
      script.textContent = old.textContent;
      old.replaceWith(script);
    });
    const loader = [...document.scripts].map((s) => s.src).find((src) => /judgeme.*loader\.js/.test(src));
    if (!loader || !window.jdgm) return;
    window.jdgm._loaderExecuted = false;
    const script = document.createElement('script');
    script.src = loader + (loader.includes('?') ? '&' : '?') + 'tq-popup=' + Date.now();
    document.head.appendChild(script);
  };

  const load = async () => {
    if (loaded) return;
    loaded = true;
    const body = dialog.querySelector('.tq-reviews-modal__body');
    try {
      const response = await fetch(root + '?section_id=torque-reviews-modal');
      if (!response.ok) throw new Error(response.status);
      body.innerHTML = await response.text();
      hydrateJudgeMe(body);
    } catch (error) {
      loaded = false;
      body.innerHTML = '<p class="tq-reviews-modal__status" role="status">Reviews could not load here. Use the link below to open the reviews page.</p>';
    }
  };

  const open = (trigger) => {
    lastTrigger = trigger;
    if (!dialog) build((trigger && trigger.getAttribute('href')) || root + 'pages/reviews');
    if (!dialog.open) {
      dialog.showModal();
      document.documentElement.classList.add('tq-reviews-open');
      overlayObserver.observe(document.body, { childList: true });
      adoptOverlays();
      dialog.querySelector('.tq-reviews-modal__close').focus();
    }
    load();
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-reviews-open]');
    if (opener) {
      if (typeof HTMLDialogElement === 'undefined') return;
      event.preventDefault();
      open(opener);
      return;
    }
    const link = event.target.closest('[data-reviews-link]');
    if (!link) return;
    const section = [...document.querySelectorAll(TESTIMONIALS)].find((el) => el.getClientRects().length > 0);
    if (section) {
      event.preventDefault();
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (section.id) history.replaceState(null, '', '#' + section.id);
    } else if (typeof HTMLDialogElement !== 'undefined') {
      event.preventDefault();
      open(link);
    }
  });

  window.TorqueReviewsModal = { open };
})();
